import type { ElementRef } from '../../lib/teact/teact';
import {
  memo, useEffect, useLayoutEffect, useMemo, useRef, useState, useUnmountCleanup,
} from '../../lib/teact/teact';
import { toggleExtraClass } from '../../lib/teact/teact-dom';
import { getGlobal } from '../../global';

import type { ApiSticker } from '../../api/types';
import type { ObserveFn } from '../../hooks/useIntersectionObserver';

import { getStickerMediaHash } from '../../global/helpers';
import { selectIsAlwaysHighPriorityEmoji } from '../../global/selectors';
import { IS_ANDROID, IS_IOS, IS_WEBM_SUPPORTED } from '../../util/browser/windowEnvironment';
import buildClassName from '../../util/buildClassName';
import {
  captureFirstFrame, getCachedFirstFrame, registerFrameCapturer, unregisterFrameCapturer,
} from '../../util/mediaFirstFrameCache';
import * as mediaLoader from '../../util/mediaLoader';
import { getStaticMediaProxyUrl, hasLoadedStaticProxyUrl, markStaticProxyUrlLoaded } from '../../util/mediaProxyUrl';

import useThumbnail from '../../hooks/media/useThumbnail';
import useColorFilter from '../../hooks/stickers/useColorFilter';
import useBrokenMediaSrc from '../../hooks/useBrokenMediaSrc';
import useCoordsInSharedCanvas from '../../hooks/useCoordsInSharedCanvas';
import useFlag from '../../hooks/useFlag';
import { useIsIntersecting } from '../../hooks/useIntersectionObserver';
import useLastCallback from '../../hooks/useLastCallback';
import useMedia from '../../hooks/useMedia';
import useMediaTransition from '../../hooks/useMediaTransition';
import useMountAfterHeavyAnimation from '../../hooks/useMountAfterHeavyAnimation';
import useUniqueId from '../../hooks/useUniqueId';
import useDevicePixelRatio from '../../hooks/window/useDevicePixelRatio';

import OptimizedVideo from '../ui/OptimizedVideo';
import AnimatedSticker from './AnimatedSticker';

import styles from './StickerView.module.scss';

type OwnProps = {
  containerRef: ElementRef<HTMLDivElement>;
  sticker: ApiSticker;
  thumbClassName?: string;
  fullMediaHash?: string;
  fullMediaClassName?: string;
  isSmall?: boolean;
  size?: number;
  customColor?: string;
  loopLimit?: number;
  shouldLoop?: boolean;
  shouldPreloadPreview?: boolean;
  skipPreview?: boolean;
  forceAlways?: boolean;
  forceOnHeavyAnimation?: boolean;
  observeIntersectionForLoading?: ObserveFn;
  observeIntersectionForPlaying?: ObserveFn;
  noLoad?: boolean;
  noPlay?: boolean;
  forceAnimatedStickerOnEnd?: boolean;
  noVideoOnMobile?: boolean;
  withSharedAnimation?: boolean;
  sharedCanvasRef?: ElementRef<HTMLCanvasElement>;
  withTranslucentThumb?: boolean; // With shared canvas thumbs are opaque by default to provide better transition effect
  onVideoEnded?: AnyToVoidFunction;
  onAnimatedStickerLoop?: AnyToVoidFunction;
  onAnimatedStickerFrame?: (frame: number) => void;
  onAnimatedStickerLoad?: AnyToVoidFunction;
};

const SHARED_PREFIX = 'shared';
// Bound on the recover-from-a-revoked-URL retries below, so media that is genuinely gone (rather than
// merely evicted) settles into the hidden state instead of re-fetching forever.
const MAX_MEDIA_RETRIES = 2;
const STICKER_SIZE = 24;

const StickerView = ({
  containerRef,
  sticker,
  thumbClassName,
  fullMediaHash,
  fullMediaClassName,
  isSmall,
  size = STICKER_SIZE,
  customColor,
  loopLimit,
  shouldLoop = false,
  shouldPreloadPreview,
  skipPreview,
  forceAlways,
  forceOnHeavyAnimation,
  observeIntersectionForLoading,
  observeIntersectionForPlaying,
  noLoad,
  noPlay,
  noVideoOnMobile,
  forceAnimatedStickerOnEnd,
  withSharedAnimation,
  withTranslucentThumb,
  sharedCanvasRef,
  onVideoEnded,
  onAnimatedStickerLoop,
  onAnimatedStickerFrame,
  onAnimatedStickerLoad,
}: OwnProps) => {
  const {
    id, isLottie, stickerSetInfo, emoji,
  } = sticker;
  const [isVideoBroken, markVideoBroken] = useFlag();
  const isUnsupportedVideo = sticker.isVideo && (
    !IS_WEBM_SUPPORTED
    || (noVideoOnMobile && (IS_IOS || IS_ANDROID))
  );
  const isVideo = sticker.isVideo;
  const isStatic = !isLottie && !isVideo;
  const previewMediaHash = getStickerMediaHash(sticker, 'preview');
  // Static stickers can render from the same-origin media proxy as a native `<img>`, so the browser
  // HTTP-caches them and never re-downloads a Blob on re-mount — the way chat photos load. When it is
  // available, skip both the preview and full worker/blob fetches (only the inline thumb is kept). If
  // the proxy URL fails to load (e.g. a stale/absent `cdnUrl`), `isStaticProxyFailed` drops us back to
  // the worker/blob path, which can still resolve the file from the media registry.
  const [isStaticProxyFailed, markStaticProxyFailed] = useFlag();
  const staticProxyUrl = isStatic && !isStaticProxyFailed ? getStaticMediaProxyUrl(sticker.cdnUrl) : undefined;
  const isStaticProxyKnownLoaded = hasLoadedStaticProxyUrl(staticProxyUrl);

  const dpr = useDevicePixelRatio();

  const filterStyle = useColorFilter(customColor);

  const isIntersectingForLoading = useIsIntersecting(containerRef, observeIntersectionForLoading);
  const shouldLoad = isIntersectingForLoading && !noLoad;
  const isIntersectingForPlaying = (
    useIsIntersecting(containerRef, observeIntersectionForPlaying)
    && isIntersectingForLoading
  );
  const shouldPlay = isIntersectingForPlaying && !noPlay;
  const hasIntersectedForPlayingRef = useRef(isIntersectingForPlaying);
  if (!hasIntersectedForPlayingRef.current && isIntersectingForPlaying) {
    hasIntersectedForPlayingRef.current = true;
  }

  const cachedPreview = mediaLoader.getFromMemory(previewMediaHash);
  // A static proxy URL that already decoded this session paints instantly from the browser HTTP
  // cache, so skip the intersection + heavy-animation gates for it — a re-mounted sticker (chat
  // switching, scrolling back) renders in the first pass instead of flashing its thumb first
  const isReadyToMountFullMedia = useMountAfterHeavyAnimation(hasIntersectedForPlayingRef.current)
    || isStaticProxyKnownLoaded;
  const shouldForcePreview = !skipPreview && (isUnsupportedVideo || (isStatic ? isSmall : noPlay));
  const shouldLoadPreview = !staticProxyUrl && !skipPreview && !customColor && !cachedPreview
    && (!isReadyToMountFullMedia || shouldForcePreview);
  // Bumped when a media URL fails to load. The usual cause is a Blob URL the in-memory cache revoked
  // while this tile still pointed at it — routine during a fast picker scroll, when loading new
  // stickers evicts the ones just scrolled past. Marking the tile broken hides the browser's glyph but
  // leaves it empty forever, because nothing re-reads the media; bumping the buster re-runs the fetch,
  // which resolves from Cache Storage (no network) and hands back a fresh URL, so the tile recovers.
  const [mediaRetryCount, setMediaRetryCount] = useState(0);
  const retryMedia = useLastCallback(() => {
    setMediaRetryCount((count: number) => (count >= MAX_MEDIA_RETRIES ? count : count + 1));
  });

  const previewMediaData = useMedia(previewMediaHash, !shouldLoadPreview, undefined, undefined, mediaRetryCount);
  const withPreview = !skipPreview && (shouldLoadPreview || cachedPreview);

  const shouldSkipLoadingFullMedia = Boolean(shouldForcePreview || (
    fullMediaHash === previewMediaHash && (cachedPreview || previewMediaData)
  ));
  const fullMediaData = useMedia(
    fullMediaHash || `sticker${id}`, !shouldLoad || shouldSkipLoadingFullMedia || Boolean(staticProxyUrl),
    undefined, undefined, mediaRetryCount,
  );
  const fullMediaSource = staticProxyUrl || fullMediaData;
  // A static `<img>` that errors on both the proxy and the worker/blob source is hidden so the thumb
  // shows instead of the browser's broken-image icon.
  const [isStaticMediaBroken, markStaticMediaBroken] = useFlag();
  const shouldRenderFullMedia = isReadyToMountFullMedia && Boolean(fullMediaSource)
    && !isVideoBroken && !isStaticMediaBroken;
  const [isPlayerReady, markPlayerReady] = useFlag();
  // Seed from the session cache: a proxy URL that already decoded once is mounted already-loaded, so
  // its transition opens with `noMountTransition` (no crossfade) — it paints instantly from the HTTP
  // cache instead of flashing blank then fading in on every remount.
  const [isStaticProxyLoaded, markStaticProxyLoaded] = useFlag(isStaticProxyKnownLoaded);
  // The native proxy `<img>` reports readiness via `onLoad`; a blob (or non-proxy static) is already
  // decoded by the time its data resolves, so it is ready as soon as it renders.
  const isFullMediaReady = shouldRenderFullMedia && (
    isStatic ? (staticProxyUrl ? isStaticProxyLoaded : true) : isPlayerReady
  );

  const thumbDataUri = useThumbnail(sticker.thumbnail);
  // The frame captured when this sticker last unmounted; without it an animated sticker re-mounts
  // blank (the backend sends no sticker thumbnails) until its player re-initializes
  const cachedFrame = !isStatic && !sharedCanvasRef ? getCachedFirstFrame(`sticker${id}`) : undefined;
  const thumbData = !skipPreview ? (cachedFrame || cachedPreview || previewMediaData || thumbDataUri) : undefined;
  // Hide the thumb if its Blob URL was revoked out from under it (media cache eviction) instead of
  // showing the browser's broken-image icon — the frequent cause of "vỡ" tiles when reopening a picker.
  const [isThumbBroken, markThumbBroken] = useBrokenMediaSrc(typeof thumbData === 'string' ? thumbData : undefined);
  const isThumbOpaque = sharedCanvasRef && !withTranslucentThumb;

  // Revealed only once the thumb has actually decoded, not merely once its url exists. A url can fail
  // — a Blob the media cache revoked mid-scroll is the common case — and an `<img>` that is already
  // visible when that happens paints the browser's broken-image glyph for a frame or more. Gating on
  // the load event means a failed thumb simply never appears, leaving the tile's own skeleton on show.
  const [isThumbLoaded, markThumbLoaded] = useFlag();

  const noCrossTransition = Boolean(isLottie && withPreview);
  const { ref: thumbRef } = useMediaTransition<HTMLImageElement>({
    hasMediaData: Boolean(thumbData && isThumbLoaded && !isThumbBroken && !isFullMediaReady),
    noCloseTransition: noCrossTransition,
  });
  const { ref: fullMediaRef } = useMediaTransition<HTMLElement>({
    hasMediaData: isFullMediaReady,
    noOpenTransition: noCrossTransition,
  });

  // Show a subtle skeleton on the picker tile (`isSmall`) while its sticker is still decoding, so a
  // fast scroll shows a shimmering placeholder instead of an empty box. The backend sends no sticker
  // thumbnail to fill that gap, and animated stickers paint onto a shared canvas (no per-tile element
  // to gate CSS on), so the class is toggled here where readiness is known. A CSS animation-delay
  // keeps it invisible for the first 150ms, so stickers that resolve from cache never flash it.
  useLayoutEffect(() => {
    const el = containerRef?.current;
    if (!el || !isSmall) return undefined;
    toggleExtraClass(el, 'is-sticker-loading', !isFullMediaReady);
    return () => {
      if (el) toggleExtraClass(el, 'is-sticker-loading', false);
    };
  }, [isFullMediaReady, isSmall, containerRef]);

  // Capture the frame being shown when this sticker leaves the screen, so its next mount paints it
  // immediately instead of a blank box while the player re-initializes
  const captureCurrentFrame = useLastCallback(() => {
    if (isStatic || sharedCanvasRef) return;
    const el = fullMediaRef.current;
    const source = el instanceof HTMLVideoElement ? el : el?.querySelector('canvas');
    if (source) {
      captureFirstFrame(source, `sticker${id}`, true);
    }
  });

  useUnmountCleanup(captureCurrentFrame);

  const coords = useCoordsInSharedCanvas(containerRef, sharedCanvasRef);

  // Preload preview for Message Input and local message
  useMedia(previewMediaHash, !shouldLoad || !shouldPreloadPreview);

  const randomIdPrefix = useUniqueId();

  // Also snapshot this sticker if the page is hidden/reloaded while it is still on screen — a reload
  // never runs the unmount capture above, so without this its frame would be missing on the next boot.
  useEffect(() => {
    if (isStatic || sharedCanvasRef) return undefined;
    registerFrameCapturer(randomIdPrefix, captureCurrentFrame);
    return () => unregisterFrameCapturer(randomIdPrefix);
  }, [isStatic, sharedCanvasRef, randomIdPrefix, captureCurrentFrame]);
  const renderId = useMemo(() => ([
    (withSharedAnimation ? SHARED_PREFIX : randomIdPrefix),
    id,
    size,
    (withSharedAnimation ? customColor : undefined),
    dpr,
  ].filter(Boolean).join('_')
  ), [id, size, customColor, dpr, withSharedAnimation, randomIdPrefix]);

  const handleAnimatedStickerLoad = useLastCallback(() => {
    onAnimatedStickerLoad?.();
    markPlayerReady();
  });

  const handleVideoBroken = useLastCallback(() => {
    markVideoBroken();
    retryMedia();
  });

  const handleStaticProxyLoad = useLastCallback(() => {
    markStaticProxyUrlLoaded(staticProxyUrl);
    markStaticProxyLoaded();
  });

  const handleThumbError = useLastCallback(() => {
    markThumbBroken();
    retryMedia();
  });

  const handleStaticImageError = useLastCallback(() => {
    // Proxy URL failed → retry via the worker/blob path; if the blob fails too, hide the frame so the
    // thumb shows instead of the browser's broken-image icon.
    if (staticProxyUrl) {
      markStaticProxyFailed();
    } else {
      markStaticMediaBroken();
      retryMedia();
    }
  });

  return (
    <>
      <img
        ref={thumbRef}
        src={isThumbBroken ? undefined : thumbData}
        className={buildClassName(
          styles.thumb,
          noCrossTransition && styles.noTransition,
          isThumbOpaque && styles.thumbOpaque,
          thumbClassName,
          'sticker-media',
        )}
        style={filterStyle}
        alt=""
        draggable={false}
        onLoad={markThumbLoaded}
        onError={handleThumbError}
      />
      {shouldRenderFullMedia && (isLottie ? (
        <AnimatedSticker
          ref={fullMediaRef as ElementRef<HTMLDivElement>}
          key={renderId}
          renderId={renderId}
          size={size}
          className={buildClassName(
            styles.media,
            (noCrossTransition || isThumbOpaque) && styles.noTransition,
            fullMediaClassName,
          )}
          tgsUrl={fullMediaData}
          play={shouldPlay}
          seekToEnd={forceAnimatedStickerOnEnd}
          noLoop={!shouldLoop}
          forceOnHeavyAnimation={forceAlways || forceOnHeavyAnimation}
          forceAlways={forceAlways}
          isLowPriority={isSmall && !selectIsAlwaysHighPriorityEmoji(getGlobal(), stickerSetInfo)}
          sharedCanvas={sharedCanvasRef?.current || undefined}
          sharedCanvasCoords={coords}
          onLoad={handleAnimatedStickerLoad}
          onLoop={onAnimatedStickerLoop}
          onEnded={onAnimatedStickerLoop}
          onFrame={onAnimatedStickerFrame}
          color={customColor}
        />
      ) : isVideo ? (
        <OptimizedVideo
          ref={fullMediaRef as ElementRef<HTMLVideoElement>}
          canPlay={shouldPlay}
          className={buildClassName(styles.media, fullMediaClassName, 'sticker-media')}
          src={fullMediaData}
          playsInline
          muted
          loop={shouldLoop && !loopLimit}
          isPriority={forceAlways}
          disablePictureInPicture
          onReady={markPlayerReady}
          onBroken={handleVideoBroken}
          onEnded={onVideoEnded}
          style={filterStyle}
        />
      ) : (
        <img
          ref={fullMediaRef as ElementRef<HTMLImageElement>}
          className={buildClassName(styles.media, fullMediaClassName, 'sticker-media')}
          src={fullMediaSource}
          // Request the proxy image with CORS so the service worker persists it to Cache Storage and
          // serves re-mounts from disk instead of re-downloading from the server (mirrors `Photo`).
          crossOrigin={staticProxyUrl ? 'anonymous' : undefined}
          alt={emoji}
          style={filterStyle}
          draggable={false}
          onLoad={staticProxyUrl ? handleStaticProxyLoad : undefined}
          onError={handleStaticImageError}
        />
      ))}
    </>
  );
};

export default memo(StickerView);
